import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Company } from '../../../interfaces/entities/company';
import { uploadImage } from '../../../services/imageUploaderService';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyForm } from './companyForm';
import { defaultCompany } from '../../../defaultValues/defaultCompany';
import { LoadingIndicator } from '../../shared/alerts/loadingIndicator';
import { ErrorDisplay } from '../../shared/alerts/errorDisplay';
import { useGetCompanyByIdQuery, useUpdateCompanyMutation } from '../../../api/companyApi';
import { useToast } from '../../../hooks/useToast';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';
import { useLoader } from '../../../hooks/useLoader';
import { getCompanyImagePath } from '../../../services/storageService';

export const CompanyEditContainer: React.FC = () => {
    const { user, currentUserIsOwnerOf } = useAuthenticatedUser();
    const { companyId } = useParams<{ companyId?: string }>(); // Mark id as optional
    const navigate = useNavigate();
    const compId = companyId ?? ""; // Ensure id is always a string
    const [companyImage, setCompanyImage] = useState<File | null>(null);

    const { data: company, isLoading: loadingCompany, error } = useGetCompanyByIdQuery(compId || "");
    const [updateCompany, updateProcess] = useUpdateCompanyMutation();

    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    const handleImageChange = (file: File | null) => {
        setCompanyImage(file);
    };

    const handleClose = () => {
        navigate(-1); // Navigate back to previous page
    };
    // Handle form submission
    const handleFormSubmit = async (values: Company) => {
        try {
            showLoader();
            const comapanyData = { ...values };

            if (companyImage) {
                const imageUrl = await uploadImage(getCompanyImagePath(comapanyData.id), companyImage);
                comapanyData.image = imageUrl;
            }
            // Step 2: Save company data in Firestore and get the document ID
            await updateCompany({
                companyId: compId,
                data: comapanyData
            }).unwrap();

            showSuccess("Company updated successfully!");
            navigate(-1); // Navigate back to previous page

        } catch (error: any) {
            console.error('Error while updating company:', error);
            showError(`${error.message}`);
        } finally {
            hideLoader();
        }
    };
    const isLoading = updateProcess.isLoading || loadingCompany;

    if (isLoading) return <LoadingIndicator message="Loading company data..." />;
    if (error) return <ErrorDisplay message="Error fetching company data." />;
    if (!user || !currentUserIsOwnerOf(companyId)) return <div>Unauthorized</div>;

    // Include roles in initialValues
    const initialValues: Company = {
        ...defaultCompany(user.id),
        ...(company || {}),
    };

    return (
        <Box>
            <CompanyForm
                title='Update Company Details'
                isSubmitting={isLoading}
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                onImageChange={handleImageChange}
                onClose={handleClose}
            />
        </Box>
    );
};
